import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import useAnalytics from '../useAnalytics/useAnalytics';
import { RootState } from '~/store';

interface EngagementTrackingOptions {
  trackActiveUsers?: boolean;
  trackEngagementSessions?: boolean;
}

interface SessionData {
  sessionId: string;
  startTime: number;
  pagesVisited: Set<string>;
  actionsPerformed: number;
}

const useEngagementTracking = (options: EngagementTrackingOptions = {}) => {
  const { track } = useAnalytics();
  const sessionData = useRef<SessionData | null>(null);
  const lastActiveDate = useRef<string>('');
  const lastActiveWeek = useRef<string>('');
  const lastActiveMonth = useRef<string>('');

  const { trackActiveUsers = true, trackEngagementSessions = true } = options;

  // Get ecosystem and organization data from Redux store
  const selectedEcosystem = useSelector(
    (state: RootState) => state.ecosystem.selectedEcosystem,
  );
  const selectedUserProfile = useSelector(
    (state: RootState) => state.auth.selectedUserProfile,
  );

  // Helper function to get common properties
  const getCommonProperties = useCallback(() => {
    const ecosystem_id =
      selectedEcosystem?._id || selectedUserProfile?.ecosystem;
    const ecosystem_name =
      selectedEcosystem?.name || selectedUserProfile?.ecosystemSummary?.name;
    const org_id = selectedUserProfile?.organization;
    const org_name = (selectedUserProfile?.userOrganization as any)?.name;
    const user_id = selectedUserProfile?._id;

    return {
      ecosystem_id,
      ecosystem_name,
      org_id,
      org_name,
      user_id,
    };
  }, [selectedEcosystem, selectedUserProfile]);

  // Helper functions for date formatting
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0]; // YYYY-MM-DD
  };

  const getWeekStart = useCallback((date: Date): string => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    const monday = new Date(d.setDate(diff));
    return formatDate(monday);
  }, []);

  const formatMonth = (date: Date): string => {
    return date.toISOString().substring(0, 7); // YYYY-MM
  };

  // Track active users (daily, weekly, monthly)
  const trackActiveUser = useCallback(() => {
    if (!trackActiveUsers || !selectedUserProfile?._id) return;

    const now = new Date();
    const today = formatDate(now);
    const thisWeek = getWeekStart(now);
    const thisMonth = formatMonth(now);
    const commonProps = getCommonProperties();

    // Remove undefined values
    const cleanProps = Object.fromEntries(
      Object.entries(commonProps).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );

    // Track daily active user (once per day)
    if (lastActiveDate.current !== today) {
      lastActiveDate.current = today;
      track('user_daily_active', {
        timestamp: Date.now(),
        date: today,
        ...cleanProps,
      } as any);
    }

    // Track weekly active user (once per week)
    if (lastActiveWeek.current !== thisWeek) {
      lastActiveWeek.current = thisWeek;
      track('user_weekly_active', {
        timestamp: Date.now(),
        week_start: thisWeek,
        ...cleanProps,
      } as any);
    }

    // Track monthly active user (once per month)
    if (lastActiveMonth.current !== thisMonth) {
      lastActiveMonth.current = thisMonth;
      track('user_monthly_active', {
        timestamp: Date.now(),
        month: thisMonth,
        ...cleanProps,
      } as any);
    }
  }, [track, trackActiveUsers, selectedUserProfile, getCommonProperties]);

  // Start engagement session
  const startEngagementSession = useCallback(() => {
    if (!trackEngagementSessions || !selectedUserProfile?._id) return;

    const sessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    sessionData.current = {
      sessionId,
      startTime: Date.now(),
      pagesVisited: new Set(),
      actionsPerformed: 0,
    };
  }, [trackEngagementSessions, selectedUserProfile]);

  // Track page visit in current session
  const trackPageVisit = useCallback((pageName: string) => {
    if (!sessionData.current) return;

    sessionData.current.pagesVisited.add(pageName);
  }, []);

  // Track action in current session
  const trackAction = useCallback(() => {
    if (!sessionData.current) return;

    sessionData.current.actionsPerformed += 1;
  }, []);

  // End engagement session
  const endEngagementSession = useCallback(() => {
    if (
      !trackEngagementSessions ||
      !sessionData.current ||
      !selectedUserProfile?._id
    )
      return;

    const session = sessionData.current;
    const duration = Date.now() - session.startTime;
    const commonProps = getCommonProperties();

    // Remove undefined values
    const cleanProps = Object.fromEntries(
      Object.entries(commonProps).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );

    track('engagement_session', {
      timestamp: Date.now(),
      session_id: session.sessionId,
      total_duration_ms: duration,
      total_duration_minutes: Math.round(duration / 60000),
      pages_visited: session.pagesVisited.size,
      actions_performed: session.actionsPerformed,
      ...cleanProps,
    } as any);

    sessionData.current = null;
  }, [
    track,
    trackEngagementSessions,
    selectedUserProfile,
    getCommonProperties,
  ]);

  // Initialize tracking when user is authenticated
  useEffect(() => {
    if (selectedUserProfile?._id) {
      trackActiveUser();
      startEngagementSession();
    }

    // Cleanup on unmount or user change
    return () => {
      if (sessionData.current) {
        endEngagementSession();
      }
    };
  }, [
    selectedUserProfile?._id,
    trackActiveUser,
    startEngagementSession,
    endEngagementSession,
  ]);

  // Track active user periodically (every 5 minutes)
  useEffect(() => {
    if (!selectedUserProfile?._id) return;

    const interval = setInterval(() => {
      trackActiveUser();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [selectedUserProfile?._id, trackActiveUser]);

  return {
    trackPageVisit,
    trackAction,
    startEngagementSession,
    endEngagementSession,
    trackActiveUser,
  };
};

export default useEngagementTracking;
